import React, { useEffect, useState } from 'react';
import { Box, Divider, Skeleton, SxProps, Typography, useTheme } from '@mui/material';
import { useSociete, useTiersService, useUtilisateurService } from '@europrocurement/l2d-domain';
import { type UtilisateursJsonldUtilisateurRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { isWithinWorkingHours } from '@europrocurement/l2d-utils';
import { type GenericModalMailType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import {
    type ContactsContactJsonldContactRead,
    type UtilisateurPrescripteurReadCommerciaux,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { CustomAvatar } from '../../molecules';
import { FlexyButton, InformationChip, LinkCallablePhone } from '../../atoms';
import { SkeletonLoader } from '../../skeletons';

export type userTypeType = 'CONTACT' | 'UTILISATEUR' | 'COMMERCIAL';
export type SourceType = 'FRONT' | 'BACK';

export type UserCardUserType = {
    mail?: string | null;
    phone?: string | null;
    mobilePhone?: string | null;
    firstName?: string;
    lastName?: string;
    userIdentifier?: string;
    serviceLibelle?: string | null;
    poste?: string | null;
};
export type UserContactCardProps = {
    source: SourceType;
    userId: number;
    userProps?: UtilisateursJsonldUtilisateurRead;
    displayUserSchedule: boolean;
    userType: userTypeType;
    displayActionContact: boolean;
    isEssentielOffer?: boolean;
    onActionClick?: (props?: unknown) => void;
    defaultMailAction?: (props?: GenericModalMailType) => void;
    mailSubject?: string;
    tiersIds?: number[];
    label?: string;
    headerBackAction?: () => void;
    headerCloseAction?: () => void;
    initialUserData?: UserCardUserType;
    errorHeaderText?: string;
};

const UserContactCard: React.FunctionComponent<UserContactCardProps> = function ({
    source,
    userId,
    userProps,
    userType,
    displayUserSchedule,
    displayActionContact = false,
    isEssentielOffer = false,
    onActionClick,
    defaultMailAction,
    mailSubject,
    tiersIds,
    label = 'Contacter',
    headerBackAction,
    headerCloseAction,
    initialUserData,
    errorHeaderText,
}) {
    const hideContactInfo = source === 'FRONT' && isEssentielOffer;

    // Find User from id Props ?
    const { palette } = useTheme();
    const { utilisateurApi } = useUtilisateurService();
    const { contactsApi } = useTiersService();
    const keycloakService = UseKeycloakService();
    const xIdSociete = useSociete();

    const [user, setUser] = useState<
        | UtilisateursJsonldUtilisateurRead
        | UtilisateurPrescripteurReadCommerciaux
        | ContactsContactJsonldContactRead
        | undefined
    >(userProps);
    const [displayedUser, setDisplayedUser] = useState<UserCardUserType>();
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const { apiErrorMessageSnackbar } = useHandleError();

    const isContactDisabled = isLoading || hasError || !displayedUser || !displayedUser.mail;
    const showErrorState = hasError || (!isLoading && !displayedUser && !initialUserData);

    useEffect(() => {
        if (initialUserData) {
            setDisplayedUser(initialUserData);
            setIsLoading(false);
            return;
        }

        if (!userProps && userId) {
            setIsLoading(true);
            setHasError(false);

            const fetchUser =
                userType === 'CONTACT'
                    ? contactsApi.getContactContactsItem({ id: userId.toString(), xIdSociete })
                    : utilisateurApi.apiUtilisateursIdGet({ id: userId.toString() });

            fetchUser
                .then((res) => setUser(res.data))
                .catch((err) => {
                    setHasError(true);
                    apiErrorMessageSnackbar(
                        err,
                        "Une erreur est survenue lors de la récupération de l'utilisateur",
                    );
                })
                .finally(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Hydrate data from user based on user Type and source (front or back)
    useEffect(() => {
        if (user) {
            setDisplayedUser({});
            let mobilePhone;
            let mail;
            let serviceLibelle;
            let poste;
            let phone;
            let firstName;
            let lastName;

            if (userType === 'COMMERCIAL') {
                const commercial = user as UtilisateurPrescripteurReadCommerciaux;
                mail = commercial.email;
                poste = commercial.poste;
                phone = commercial.ligneDirecte;
                firstName = commercial.prenom;
                lastName = commercial.nom;
                serviceLibelle = 'Service Commercial';
                if (source === 'BACK') {
                    mobilePhone = commercial.portable;
                }
            } else if (userType === 'CONTACT') {
                const contact = user as ContactsContactJsonldContactRead;
                const contactMail = contact.emailsContact.find(
                    (contactMails) => contactMails.defaut && contactMails.email,
                );
                if (contactMail) {
                    mail = contactMail.email;
                }
                const contactPhone = contact.telephonesContact.find(
                    (contactPhones) => contactPhones.defaut && contactPhones.telephone,
                );
                if (contactPhone) {
                    phone = contactPhone.telephone;
                }
                firstName = contact.prenomContact;
                lastName = contact.nomContact;
            } else {
                const utilisateur = user as UtilisateursJsonldUtilisateurRead;
                mail = utilisateur.mail;
                phone = utilisateur.ligneDirecte;
                if (utilisateur.groupeTravail && utilisateur.groupeTravail.length > 0) {
                    const activGroup = utilisateur.groupeTravail.find(
                        (group) => group.fDelete === false,
                    );
                    if (activGroup?.email) {
                        mail = activGroup?.email;
                    }
                    if (activGroup?.phone) {
                        phone = activGroup?.phone;
                    }
                }
                serviceLibelle = utilisateur.service?.libelle;
                poste = utilisateur.poste;
                firstName = utilisateur.prenom;
                lastName = utilisateur.nom;
                if (source === 'BACK') {
                    mobilePhone = utilisateur.portable;
                }
            }

            setDisplayedUser({
                mail,
                phone,
                firstName,
                lastName,
                userIdentifier: user.userIdentifier ?? undefined,
                mobilePhone,
                serviceLibelle,
                poste,
            });
        }
    }, [user, userType, source]);

    const handleButtonClick = () => {
        if (displayedUser && displayedUser.mail) {
            if (typeof onActionClick !== 'undefined') {
                onActionClick();
            } else if (typeof defaultMailAction !== 'undefined') {
                let mailBodyContent = `Bonjour ${displayedUser.firstName}  ${displayedUser.lastName}\n\n\n\n`;
                mailBodyContent += `Cordialement,\n${keycloakService.getName()}\n${keycloakService.getEmail()}`;

                defaultMailAction({
                    to: [
                        {
                            firstName: displayedUser.firstName as string,
                            lastName: displayedUser.lastName as string,
                            emailAddress: displayedUser.mail,
                        },
                    ],
                    subject: mailSubject,
                    body: mailBodyContent,
                    showBody: true,
                    showSubject: true,
                    showCC: true,
                    searchParamsTiersIds: tiersIds,
                });
            }
        }
    };

    const flexCenter: SxProps = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

    const isWithinWorkingHour =
        isWithinWorkingHours(9 * 60, 17 * 60) && !isWithinWorkingHours(12 * 60, 14 * 60);

    const renderHeaderTitle = () => {
        if (showErrorState && errorHeaderText) {
            return (
                <Typography
                    variant="l2dh6"
                    sx={{
                        display: 'flex',
                        textAlign: 'start',
                        color: palette.error.main,
                    }}
                >
                    {errorHeaderText}
                </Typography>
            );
        }

        if (displayedUser?.serviceLibelle) {
            return <Typography variant="l2dh6">{displayedUser.serviceLibelle}</Typography>;
        }
        return (
            <Skeleton
                variant="text"
                width={200}
                height={45}
            />
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                width: '332px',
                position: 'relative',
            }}
        >
            {/* Title and actions */}
            {(headerBackAction ||
                headerCloseAction ||
                showErrorState ||
                displayedUser?.serviceLibelle) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    {headerBackAction && (
                        <Box
                            onClick={headerBackAction}
                            sx={{
                                cursor: 'pointer',
                                color: palette.secondary.main,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <FaOptionIcon icon="arrow-left" />
                        </Box>
                    )}
                    {renderHeaderTitle()}
                    {headerCloseAction && (
                        <>
                            <Box sx={{ flex: 1 }} />
                            <Box
                                onClick={headerCloseAction}
                                sx={{
                                    cursor: 'pointer',
                                    color: palette.grey[500],
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <FaOptionIcon
                                    icon="times"
                                    size="lg"
                                />
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* Header avatar / name Status */}
            <SkeletonLoader
                isLoading={isLoading}
                isInError={showErrorState}
                errorChildren={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '24px 0',
                            gap: '8px',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: palette.grey[500] }}
                        >
                            Aucune information disponible
                        </Typography>
                    </Box>
                }
                skeleton={
                    <>
                        <Box
                            sx={{
                                ...flexCenter,
                                borderBottom: `1px solid ${palette.grey[100]}`,
                                padding: '8px 0 16px 0',
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={90}
                                height={90}
                            />
                            <Box sx={{ flex: '1', paddingLeft: '24px' }}>
                                <Box
                                    sx={{
                                        ...flexCenter,
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        gap: '4px',
                                    }}
                                >
                                    <Skeleton
                                        variant="text"
                                        width={150}
                                        height={24}
                                    />
                                    <Skeleton
                                        variant="text"
                                        width={100}
                                        height={18}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width={100}
                                        height={30}
                                        sx={{ borderRadius: '16px' }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                ...flexCenter,
                                flexDirection: 'column',
                                alignItems: 'start',
                                gap: '2px',
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={200}
                                height={28}
                            />
                            <Skeleton
                                variant="text"
                                width={200}
                                height={28}
                            />
                        </Box>
                    </>
                }
            >
                {displayedUser && (
                    <>
                        <Box
                            sx={{
                                ...flexCenter,
                                padding: '8px 0 16px 0',
                            }}
                        >
                            <CustomAvatar
                                sx={{
                                    width: '90px',
                                    height: '90px',
                                }}
                                firstName={displayedUser.firstName}
                                lastName={displayedUser.lastName}
                                keycloakId={
                                    displayedUser.userIdentifier
                                        ? displayedUser.userIdentifier
                                        : undefined
                                }
                            />
                            <Box sx={{ flex: '1', paddingLeft: '24px' }}>
                                <Box
                                    sx={{
                                        ...flexCenter,
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        gap: '4px',
                                    }}
                                >
                                    <Typography
                                        variant="l2dh7"
                                        sx={{ fontWeight: '600' }}
                                    >
                                        {displayedUser.firstName} {displayedUser.lastName}
                                    </Typography>

                                    {displayedUser.serviceLibelle && (
                                        <Typography
                                            variant="l2dcaption"
                                            sx={{ color: 'text.gris70' }}
                                        >
                                            {displayedUser.serviceLibelle}
                                        </Typography>
                                    )}

                                    <InformationChip
                                        color={
                                            isWithinWorkingHour
                                                ? palette.success.main
                                                : palette.grey[700]
                                        }
                                        label={
                                            <Box
                                                sx={{
                                                    ...flexCenter,
                                                    justifyContent: 'space-between',
                                                    gap: '4px',
                                                    padding: '6px 12px',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '15px',
                                                        height: '15px',
                                                        borderRadius: '100%',
                                                        backgroundColor: isWithinWorkingHour
                                                            ? palette.success.main
                                                            : palette.grey[700],
                                                    }}
                                                />
                                                <Typography>
                                                    {isWithinWorkingHour
                                                        ? 'Disponible'
                                                        : 'Hors ligne'}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                        {!hideContactInfo && (
                            <>
                                <Divider />
                                <Box
                                    sx={{
                                        ...flexCenter,
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        gap: '12px',
                                    }}
                                >
                                    {displayedUser?.mail && (
                                        <Box
                                            sx={{
                                                ...flexCenter,
                                                alignItems: 'start',
                                                gap: '16px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={handleButtonClick}
                                        >
                                            <Box sx={flexCenter}>
                                                <FaOptionIcon
                                                    color={palette.grey[500]}
                                                    icon="envelope"
                                                />
                                            </Box>
                                            <Typography sx={{ color: palette.primary.dark }}>
                                                {displayedUser.mail}
                                            </Typography>
                                        </Box>
                                    )}

                                    {displayedUser?.phone && (
                                        <Box
                                            sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}
                                        >
                                            <Box sx={flexCenter}>
                                                <FaOptionIcon
                                                    color={palette.grey[500]}
                                                    icon="phone"
                                                />
                                            </Box>
                                            <LinkCallablePhone phone={displayedUser.phone} />
                                        </Box>
                                    )}

                                    {source === 'BACK' && displayedUser?.poste && (
                                        <Box
                                            sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}
                                        >
                                            <Box sx={flexCenter}>
                                                <FaOptionIcon
                                                    color={palette.grey[500]}
                                                    icon="phone"
                                                />
                                            </Box>
                                            <LinkCallablePhone phone={displayedUser.poste} />
                                        </Box>
                                    )}
                                    {source === 'BACK' && displayedUser?.mobilePhone && (
                                        <Box
                                            sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}
                                        >
                                            <Box sx={flexCenter}>
                                                <FaOptionIcon
                                                    color={palette.grey[500]}
                                                    icon="phone"
                                                />
                                            </Box>

                                            <LinkCallablePhone phone={displayedUser.mobilePhone} />
                                        </Box>
                                    )}

                                    {displayUserSchedule && (
                                        <Box
                                            sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}
                                        >
                                            <Box sx={flexCenter}>
                                                <FaOptionIcon
                                                    color={palette.grey[500]}
                                                    icon="clock"
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'start',
                                                }}
                                            >
                                                <Typography
                                                    variant="l2dbodys"
                                                    color="text.gris50"
                                                >
                                                    Du lundi au vendredi
                                                </Typography>
                                                <Typography
                                                    variant="l2dbodys"
                                                    color="text.gris50"
                                                >
                                                    De 9h00 - 12h00, 14h00 - 17h00
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </SkeletonLoader>

            {displayActionContact && (
                <FlexyButton
                    variant="contained"
                    onClick={handleButtonClick}
                    disabled={isContactDisabled}
                    sx={{
                        padding: '16px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0 10px',
                        width: '100%',
                        transition: 'all ease-in-out 300ms',
                        fontSize: '16px',
                    }}
                >
                    <Typography
                        variant="button"
                        fontSize="16px"
                        color={palette.getContrastText(palette.primary.main)}
                    >
                        {label}
                    </Typography>
                    <FaOptionIcon
                        color={palette.getContrastText(palette.primary.main)}
                        icon="chevron-right"
                        size="xs"
                    />
                </FlexyButton>
            )}
        </Box>
    );
};

export { UserContactCard };
export default UserContactCard;
